import * as React from 'react';
import {
  Box,
  Step,
  StepLabel,
  Stepper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import type { StepIconProps } from '@mui/material/StepIcon';
import { GlassColors } from '../glassmorphism/GlassmorphismStyles';

const RAIL = 8;

type DotExtraProps = { size: number; isDark: boolean };

function DotIconBase(props: StepIconProps & DotExtraProps) {
  const { active, completed, className, size, isDark } = props;
  const on = !!active || !!completed;

  return (
    <Box
      className={className}
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: on ? 'rgba(41, 163, 34, 1)' : isDark ? '#1A1A1A' : '#E5E5DC',
        boxShadow: on
          ? `
            0px 0px 34.77px 0px #0000000D inset,
            0px -0.5px 0.25px 0px #00000080 inset,
            0px 0.5px 0.75px 0px #FFFFFF inset,
            0px 2.07px 3.73px 0px #0000004D,
            0px 2.08px 2.48px -2px #00000066,
            0px 3.08px 2px -2px #00000054
          `
          : `
            0px 0px 10px 0px rgba(0, 0, 0, 0.04),
            0px -0.5px 0.25px 0px rgba(0, 0, 0, 0.3),
            0px 0.5px 0.75px 0px rgba(255, 255, 255, 1),
            0px 2.07px 3.73px 0px rgba(0, 0, 0, 0.27) inset,
            0px 1.08px 2.48px 0px rgba(0, 0, 0, 0.25) inset
          `,
      }}
    />
  );
}

/**
 *  Backward compatible props:
 * - old:  activeIndex
 * - new:  activeStep
 */

type RailStepperProps = {
  isProviderFlow: boolean;
  activeStep?: number;
  activeIndex?: number;
  // Optional labels override
  steps?: string[];
  isDark?: boolean;

  onStepClick?: (index: number) => void;
  clickableUntil?: number;
};

export default function RailStepper(props: RailStepperProps) {
  const theme = useTheme();
  const derivedIsDark = theme.palette.mode === 'dark';

  const isDark = props.isDark ?? derivedIsDark;

  // activeStep
  const active = props.activeStep ?? props.activeIndex ?? 0;

  // labels:
  const labels =
    props.steps ??
    (props.isProviderFlow || active === 0
      ? ['Step 1', 'Step 2', 'Step 3']
      : ['Step 1', 'Step 2']);

  const isTabletDown = useMediaQuery(theme.breakpoints.down('tablet'));
  const DOT = isTabletDown ? 16 : 24;

  const total = labels.length;
  const safeActive = total > 0 ? Math.min(Math.max(active, 0), total - 1) : 0;
  const t = total <= 1 ? 0 : safeActive / (total - 1);

  const StepIcon = React.useCallback(
    (p: StepIconProps) => <DotIconBase {...p} size={DOT} isDark={isDark} />,
    [DOT, isDark],
  );

  return (
    <Box sx={{ width: '100%', px: 0, pb: 1 }}>
      <Box sx={{ position: 'relative', width: '100%', pt: 1 }}>
        {/* Rail background */}
        <Box
          sx={{
            position: 'absolute',
            left: { xs: '20px', tablet: DOT / 2 },
            right: { xs: '20px', tablet: DOT / 2 },
            top: (DOT - RAIL) / 2 + RAIL,
            height: RAIL,
            borderRadius: 999,
            bgcolor: isDark ? '#1A1A1A' : '#E5E5DC',
            boxShadow: `
              0px 0px 10px 0px rgba(0, 0, 0, 0.04),
              0px -0.5px 0.25px 0px rgba(0, 0, 0, 0.3),
              0px 0.5px 0.75px 0px rgba(255, 255, 255, 1),
              0px 2.07px 3.73px 0px rgba(0, 0, 0, 0.27) inset,
              0px 1.08px 2.48px 0px rgba(0, 0, 0, 0.25) inset
            `,
          }}
        />

        {/* Progress */}
        <Box
          sx={{
            position: 'absolute',
            left: { xs: '20px', tablet: DOT / 2 },
            top: (DOT - RAIL) / 2 + RAIL,
            height: RAIL,
            width: {
              xs: `calc((100% - ${DOT * 2}px) * ${t})`,
              tablet: `calc((100% - ${DOT}px) * ${t})`,
            },
            borderRadius: 999,
            transition: 'width .2s ease',
            bgcolor: 'rgba(41, 163, 34, 1)',
            boxShadow: `
              0px 0px 34.77px 0px #0000000D inset,
              0px -0.5px 0.25px 0px #00000080 inset,
              0px 0.5px 0.75px 0px #FFFFFF inset,
              0px 2.07px 3.73px 0px #0000004D,
              0px 2.08px 2.48px -2px #00000066,
              0px 3.08px 2px -2px #00000054
            `,
          }}
        />

        <Stepper
          alternativeLabel
          activeStep={safeActive}
          connector={null}
          sx={{
            px: 0,
            '& .MuiStepLabel-iconContainer': { p: 0 },
            '& .MuiStepLabel-label.Mui-completed': { opacity: 1 },
            '& .MuiStepLabel-label': {
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 500,
              lineHeight: 1.66,
              letterSpacing: '.4px',
              mt: '8px',
              fontSize: '12px',
              color: isDark
                ? GlassColors.dark.textSecondary
                : GlassColors.light.textSecondary,
            },
            '& .MuiStep-root:first-of-type .MuiStepLabel-label': {
              textAlign: 'left',
              ml: '-8px',
            },
            '& .MuiStep-root:first-of-type .MuiStepLabel-iconContainer': {
              mr: 'auto',
            },
            '& .MuiStep-root:last-child .MuiStepLabel-label': {
              textAlign: 'right',
              mr: '-8px',
            },
            '& .MuiStep-root:last-child .MuiStepLabel-iconContainer': {
              ml: 'auto',
            },
            '& .MuiStep-root': { pr: '10px', pl: '10px' },
          }}
        >
          {labels.map((label, idx) => {
            const clickable =
              !!props.onStepClick &&
              (props.clickableUntil == null || idx <= props.clickableUntil);

            return (
              <Step key={label}>
                <StepLabel
                  slots={{ stepIcon: StepIcon }}
                  onClick={() => clickable && props.onStepClick?.(idx)}
                  sx={{ cursor: clickable ? 'pointer' : 'default' }}
                >
                  {label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>
    </Box>
  );
}
